/**
 * 🔍 DIAGNÓSTICO - Problema "Usuário não encontrado"
 * 
 * Este arquivo ajuda a diagnosticar problemas de autenticação
 * e sincronização entre frontend e backend.
 */

import { validateToken, getAccessToken, isAuthenticated } from '../Backend/services/authService';
import { API_CONFIG } from '../Backend/config/api';

interface DiagnosticResult {
  timestamp: string;
  isAuthenticated: boolean;
  hasAccessToken: boolean;
  tokenValue?: string;
  apiBaseUrl: string;
  backendReachable: boolean;
  validateTokenResponse?: unknown;
  error?: string;
}

/**
 * 🔍 Executa diagnóstico completo da autenticação
 */
export async function diagnoseAuthProblem(): Promise<DiagnosticResult> {
  const result: DiagnosticResult = {
    timestamp: new Date().toISOString(),
    isAuthenticated: isAuthenticated(),
    hasAccessToken: !!getAccessToken(),
    apiBaseUrl: API_CONFIG.BASE_URL,
    backendReachable: false,
  };

  // Verificar token
  const token = getAccessToken();
  if (token) {
    result.tokenValue = `${token.substring(0, 20)}...`; // Apenas primeiros 20 chars
  }

  // Testar conectividade com backend usando endpoint público
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/health/`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    result.backendReachable = response.status < 500;
  } catch (error) {
    result.backendReachable = false;
  }

  // Testar validação de token se houver
  if (token) {
    try {
      console.log('🔍 Testando validação de token...');
      const userData = await validateToken(token);
      result.validateTokenResponse = userData;
      
      if (!userData) {
        result.error = 'Token válido mas não retornou dados do usuário';
      }
    } catch (error) {
      result.error = `Erro na validação: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
    }
  }

  return result;
}

/**
 * 🛠️ Função para corrigir problemas comuns
 */
export async function fixCommonAuthIssues(): Promise<string[]> {
  const fixes: string[] = [];

  // 1. Limpar dados corrompidos
  const sessionRestored = localStorage.getItem('sessionRestored');
  const accessToken = localStorage.getItem('accessToken');

  if (sessionRestored && !accessToken) {
    localStorage.removeItem('sessionRestored');
    fixes.push('Removido flag sessionRestored sem token');
  }

  // 2. Verificar tokens malformados
  if (accessToken) {
    try {
      // Tentar decodificar JWT básico
      const parts = accessToken.split('.');
      if (parts.length !== 3) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionRestored');
        fixes.push('Removido token malformado');
      }
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionRestored');
      fixes.push('Removido token inválido');
    }
  }

  // 3. Tentar validar novamente
  if (accessToken && isAuthenticated()) {
    try {
      const userData = await validateToken(accessToken);
      if (userData) {
        fixes.push('Token validado com sucesso - problema pode estar no endpoint');
      } else {
        fixes.push('Token expirado ou inválido - necessário novo login');
      }
    } catch (error) {
      fixes.push(`Erro na validação: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  return fixes;
}

/**
 * 🔧 Função global para debug
 */
export function setupAuthDiagnostics() {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).diagnoseAuth = diagnoseAuthProblem;
    (window as unknown as Record<string, unknown>).fixAuthIssues = fixCommonAuthIssues;
    
    // Debug functions loaded silently
  }
}

/**
 * 📊 Log detalhado do problema (com debounce para evitar duplicação)
 */
let lastAuthLogTime = 0;
const AUTH_LOG_DEBOUNCE = 5000; // 5 segundos

export async function logAuthenticationState() {
  // 🚨 OTIMIZAÇÃO: Debounce para evitar logs duplicados
  const now = Date.now();
  if (now - lastAuthLogTime < AUTH_LOG_DEBOUNCE) {
    return; // Skip se muito recente
  }
  lastAuthLogTime = now;

  // Only show auth diagnostics in dev mode or when there are actual problems
  const diagnostic = await diagnoseAuthProblem();

  if (import.meta.env.DEV || (!diagnostic.backendReachable && diagnostic.isAuthenticated)) {
    console.log('🔍 AUTH DIAGNOSTIC');
    console.log(`Auth: ${diagnostic.isAuthenticated} | Token: ${diagnostic.hasAccessToken} | Backend: ${diagnostic.backendReachable}`);

    if (diagnostic.error && import.meta.env.DEV) {
      console.error('❌ Auth Error:', diagnostic.error);
    }
    
    // Only show critical issues
    if (!diagnostic.backendReachable && import.meta.env.DEV) {
      console.log('💡 Backend offline');
    }
  }
  
  return diagnostic;
}
